import { colors } from '@design-system/styles'
import type { Theme } from '@emotion/react'
import type { Interpolation } from '@emotion/serialize'
import { default as RCTooltip } from 'rc-tooltip'
import type { TooltipProps } from 'rc-tooltip/es/Tooltip'
import React, { useEffect, useMemo, useState } from 'react'

import { Icon } from '../../atoms/Icon'
import S from './SpeechBubble.style'

export type Props = {
  children: React.ReactElement
  Overlay: React.ReactNode
  placement?:
    | 'left'
    | 'right'
    | 'top'
    | 'top-right'
    | 'top-left'
    | 'bottom'
    | 'bottom-right'
    | 'bottom-left'
  overlayBackgroundColor?: string
  overlayBorderColor?: string
  overlayColor?: string
  overlayContainerStyle?: Interpolation<Theme>
  onCloseButtonClick?: () => void
  closeButtonOpt?: {
    style?: React.CSSProperties
    hasCloseButton?: boolean
    isCloseButtonVisible?: boolean
    iconColor?: string
    iconSize?: 16 | 20
  }
  arrowStyle?: React.CSSProperties
  arrowOffset?: number
  getTooltipContainer?: TooltipProps['getTooltipContainer']
} & Omit<React.ComponentPropsWithRef<typeof RCTooltip>, 'placement' | 'overlay'>

const SpeechBubble = ({
  visible = true,
  children,
  Overlay,
  placement = 'top',
  overlayBackgroundColor = colors.blue.$100,
  overlayBorderColor = colors.blue.$200,
  overlayColor = 'inherit',
  overlayContainerStyle,
  onCloseButtonClick,
  closeButtonOpt = {
    hasCloseButton: true,
    isCloseButtonVisible: true,
    iconColor: colors.gray.$600,
    iconSize: 16,
  },
  align: _align,
  overlayStyle,
  arrowStyle,
  arrowOffset,
  getTooltipContainer,
  ...props
}: Props) => {
  const [_visible, setVisible] = useState(visible)

  useEffect(() => {
    setVisible(visible)
  }, [visible])

  const pos = useMemo(
    () =>
      placement.replace(/-(.*)/g, '') as Exclude<
        Props['placement'],
        undefined | 'bottom-right' | 'bottom-left' | 'top-right' | 'top-left'
      >,
    [placement],
  )

  const align = useMemo(() => {
    if (_align?.offset) {
      const { offset, ...rest } = _align
      const correction = pos === 'top' ? -6 : 6
      return { offset: [offset[0], offset[1] + correction], ...rest }
    }
    return _align
  }, [_align?.offset, pos])

  return (
    <RCTooltip
      getTooltipContainer={getTooltipContainer}
      placement={pos}
      destroyTooltipOnHide
      visible={visible && _visible}
      showArrow
      align={align}
      arrowContent={
        <div
          css={[
            S.arrow,
            S.arrowBorder({
              placement,
              arrowOffset,
              borderColor: overlayBorderColor,
              backgroundColor: overlayBackgroundColor,
            }),
          ]}
          style={arrowStyle}
        />
      }
      overlay={
        <S.BubbleContainer
          css={overlayContainerStyle}
          onMouseDown={(e) => e.stopPropagation()}
          onTouchStart={(e) => e.stopPropagation()}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="bubble-content" data-component="mathflat-speech-bubble">
            {Overlay}
          </div>
          {closeButtonOpt.hasCloseButton && (
            <div className="close-button-container">
              <button
                className="close-button"
                style={{
                  ...(closeButtonOpt.iconSize === 20 && {
                    padding: '16px 14px 16px 10px',
                  }),
                  ...closeButtonOpt.style,
                }}
                data-component="mathflat-speech-bubble-close-button"
                onClick={(e) => {
                  e.stopPropagation()
                  setVisible(false)
                  onCloseButtonClick?.()
                }}
              >
                {closeButtonOpt.isCloseButtonVisible && (
                  <Icon
                    name="close"
                    size={closeButtonOpt.iconSize}
                    color={closeButtonOpt.iconColor}
                  />
                )}
              </button>
            </div>
          )}
        </S.BubbleContainer>
      }
      overlayStyle={{
        position: 'relative',
        backgroundColor: overlayBackgroundColor,
        borderColor: overlayBorderColor,
        color: overlayColor,
        borderRadius: 12,
        borderWidth: 1,
        borderStyle: 'solid',
        zIndex: 98,
        ...((pos === 'top' || pos === 'bottom') && {
          [pos]: (pos === 'bottom' ? 1 : -1) * 6,
        }),
        ...overlayStyle,
      }}
      {...props}
    >
      {children}
    </RCTooltip>
  )
}

export default SpeechBubble
